import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { User } from '@models/user';
import { PermissionsService } from '@services/permissions/permissions.service';
import { TokenService } from '@services/token/token.service';
import {
  exposePermissionsDebug,
  PermissionsSnapshotSource,
} from '@utils/permissions-debug.utils';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, Observable, of, ReplaySubject } from 'rxjs';
@Injectable({
  providedIn: 'root',
})
export class SharedService {
  user?: User | null;
  public user$ = new ReplaySubject<User | null>(1);

  /** Lista de features/páginas que o usuário tem permissão */
  userFeatures: string[] | undefined = undefined;
  public userFeatures$ = new BehaviorSubject<string[] | undefined>(undefined);

  /**
   * Retorno cru do GetUserMenu. Mantido apenas para diagnóstico/log — NÃO
   * participa de nenhuma decisão de acesso: quem manda é o GetUserPermissions.
   */
  menuFeatures: string[] | undefined = undefined;

  /** Indica se as features já foram carregadas (mesmo que vazias) */
  userFeaturesLoaded = false;

  /**
   * Mapeamento de featureKey para rota.
   * Usado para determinar a home dinâmica do usuário.
   */
  private readonly featureRouteMap: Record<string, string> = {
    subscriptions: '/',
    users: '/usuarios',
    contents: '/conteudo',
    settings: '/configuracoes',
    transactions: '/transacoes-financeiras',
    cards: '/transacoes-financeiras/status-do-cartao',
    reports: '/relatorios',
    activity: '/atividades',
    help: '/ajuda',
    roles: '/gerenciar-cargos',
  };

  registerForm: User | null = null;
  web = true;
  mobileMenuIsOpen = false;
  webMenuIsExpanded = false;

  public mfaChallenge: {
    mfaSessionId?: string;
    mfaMethods?: string[];
  };

  constructor(
    private cookieService: CookieService,
    private router: Router,
    private tokenService: TokenService,
    private permissionsService: PermissionsService
  ) {
    this.user$.subscribe((user) => {
      this.user = user;
      // Nunca persistir "null"/"undefined" no cookie: o guard leria isso como sessão válida.
      if (user) {
        this.cookieService.set('user', JSON.stringify(user), { path: '/' });
      } else {
        this.cookieService.delete('user', '/');
      }
    });
    const webMenuIsExpanded = localStorage.getItem('webMenuIsExpanded');
    if (webMenuIsExpanded) {
      this.webMenuIsExpanded = webMenuIsExpanded === 'true';
    }

    // Disponibiliza `window.__permissions()` em qualquer ambiente: o log de
    // bootstrap é único e some no reload, mas o diagnóstico precisa existir
    // também em produção.
    exposePermissionsDebug(() => this.permissionsDebugSource());
  }

  /** Estado consolidado de permissões, para log e inspeção manual. */
  permissionsDebugSource(): PermissionsSnapshotSource {
    return {
      permissions: this.permissionsService.current,
      readableResources: this.permissionsService.getReadableResources(),
      menuFeatures: this.menuFeatures,
      effectiveFeatures: this.getEffectiveFeatures(),
      hasFeatureAccess: (featureKey: string) => this.hasFeatureAccess(featureKey),
    };
  }

  loadDataWithUser(user?: User): Observable<boolean> {
    this.user = user;
    this.user$.next(user ?? null);
    return of(user ? true : false);
  }

  get accessToken(): string | null {
    return this.tokenService.getAccessToken();
  }

  set accessToken(value: string | null) {
    if (value) {
      this.tokenService.setTokens(value, this.refreshToken || '');
    } else {
      this.tokenService.clearTokens();
    }
  }

  get refreshToken(): string | null {
    return this.tokenService.getRefreshToken();
  }

  set refreshToken(value: string | null) {
    if (value) {
      this.tokenService.setTokens(this.accessToken || '', value);
    }
  }

  updateCookies(): void {
    // Tokens já são sincronizados automaticamente via TokenService
    // Apenas atualiza o cookie do user
    this.cookieService.set('user', JSON.stringify(this.user) ?? '', { path: '/' });
  }

  setRedirectUrl(url: string | null): void {
    if (url === null || url === undefined) {
      this.cookieService.delete('redirectUrl', '/');
    } else {
      this.cookieService.set('redirectUrl', url, { path: '/' });
    }
  }

  getRedirectUrl(): string | null {
    const url = this.cookieService.get('redirectUrl');
    if (!url || url === 'null' || url === 'undefined') {
      return null;
    }
    return url;
  }

  /**
   * Fonte única de verdade dos módulos acessíveis.
   *
   * Regra: SOMENTE o GetUserPermissions decide. Não há fallback para o
   * GetUserMenu — sem `resource:read` o módulo não existe para o usuário.
   * Guards, menu e home dinâmica DEVEM consultar esta mesma lista;
   * divergência entre elas é o que produz loop de redirecionamento.
   */
  getEffectiveFeatures(): string[] {
    return this.permissionsService.getReadableResources();
  }

  /**
   * Retorna a rota da home dinâmica do usuário.
   * Baseada em `getEffectiveFeatures()` — a mesma lista usada pelos guards.
   * Retorna `null` quando não há nenhum módulo navegável (caso de logout).
   */
  getUserHomeRoute(): string {
    return this.resolveUserHomeRoute() ?? '/login';
  }

  /** Igual a `getUserHomeRoute()`, porém explícito quando não há home válida. */
  resolveUserHomeRoute(): string | null {
    const features = this.getEffectiveFeatures();

    if (!features.length) {
      return null;
    }

    if (features.includes('subscriptions')) {
      return this.featureRouteMap['subscriptions'];
    }

    const firstMapped = features.find((feature) => !!this.featureRouteMap[feature]);
    return firstMapped ? this.featureRouteMap[firstMapped] : null;
  }

  /**
   * Verifica se o usuário tem acesso a uma feature específica.
   *
   * Exige sempre `<resource>:read`. Nenhuma feature é liberada sem ela e
   * nenhuma herda a permissão de outro resource.
   */
  hasFeatureAccess(featureKey: string): boolean {
    return this.permissionsService.canAccessFeature(featureKey);
  }

  /** Espelha em `userFeatures` os resources legíveis do GetUserPermissions. */
  syncFeaturesFromPermissions(): void {
    const readable = this.permissionsService.getReadableResources();

    this.userFeatures = readable;
    this.userFeatures$.next(readable);
    this.userFeaturesLoaded = true;
  }

  /**
   * Limpa todos os dados de autenticação do usuário.
   * @param redirectToLogin Se true, redireciona para a página de login (padrão: true)
   */
  logoutUser(redirectToLogin: boolean = true): void {
    this.tokenService.clearTokens();
    this.user = null;
    this.user$.next(null);
    this.userFeatures = undefined;
    this.userFeatures$.next(undefined);
    this.menuFeatures = undefined;
    this.userFeaturesLoaded = false;
    this.permissionsService.clear();

    this.cookieService.delete('user', '/');
    this.cookieService.delete('redirectUrl', '/');

    if (redirectToLogin) {
      this.router.navigateByUrl('/login');
    }
  }
}
